import { IScrBaseResponse, ScrBaseResponse, ScrMessage } from './../services/base.service';
import { AfterViewInit, Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, ObservableInput, PartialObserver, Subscription, first, firstValueFrom } from 'rxjs';
import { ConfigService } from '../services/config.service';
import { TableColumn } from '../models/table-columns';
import { ScrServiceManager } from '../services/servicemanager.service';
import { forkJoin } from 'rxjs';
import { MaxLen } from '../shared/constants';
import { AbstractControl, NgForm } from '@angular/forms';
import { NGXLogger } from 'ngx-logger';
import { TranslateService } from '@ngx-translate/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Message, IuiMessagesComponent, MessageCode } from '../shared/messages/messages.component';
import { startCase } from 'lodash-es';
import { ScrEnvironment } from 'src/environments/environment';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from '@scr/ngx-intl-tel-input';
import { ExperimentalFeaturesService } from '../services/experiments.service';
import { AnalyticsService } from '../services/analytics.service';

/**
 * Structure for primeng table columns.
 */
export interface TableColumnDefinition {
  field: string;
  header: string;
  sorting?: boolean;
  width?: number;
}

export interface LabelValuePair {
  label: string;
  value: number | string | boolean;
}

export interface PrimeNgTablePageEvent {
  first: number;
  rows: number;
}

export class CustomValidationHandler {
  constructor(public key: string, public createError: (control: AbstractControl, label: string) => string) {

  }
}

export class CodeAndArgs {
  constructor(public code: string, public args?: object) { }
}


/**
 * Pages such as user management and credit card list have child views that report actions
 * back to their parent.  Use this event type to make use of the methods in this base class
 * that process the child events.
 */
export class ChildEvent {
  constructor(public action: string, public value?: string | number, public message?: string, public messageCode?: string) {

  }
}

/**
 * When observing an network observable, use these methods to be informed of the result if desired.
 */
interface MessageHooks<T> {
  /**
   * On a completely successfull request (status 200, status:success), call this.
   * Return false to tell the observe method NOT to display a success message.
   */
  onSuccess?: (msg: T) => boolean | void;

  /**
   * On a success HTTP call with status 200 but application status of failure or failed, call this.
   * Return false to tell the observe method NOT to process error messages.
   */
  onFailure?: (msg: T) => boolean | void;

  /**
   * In the event of an HTTP failure, call this if provided.
   * Return false to tell the observer that you got it covered and that the observer should not try to display a message.
   */
  onError?: (e: HttpErrorResponse) => boolean | void;
}

@Component({
  template: ''
})
export abstract class ScrBaseComponent implements OnDestroy, AfterViewInit {

  public MaxLen = MaxLen;
  public activeSub?: Subscription;

  @ViewChild(IuiMessagesComponent, { read: IuiMessagesComponent }) private msgs?: IuiMessagesComponent;

  CountryISO: typeof CountryISO = CountryISO;
  PhoneNumberFormat: typeof PhoneNumberFormat = PhoneNumberFormat;
  SearchCountryField: typeof SearchCountryField = SearchCountryField;

  public customerSupportPhone = '';

  protected experimentsService: ExperimentalFeaturesService;
  private serviceManager: ScrServiceManager;

  private isThisServiceDesk = false;

  protected _log?: NGXLogger;
  protected _translate?: TranslateService;
  protected _domSanitizer?: DomSanitizer;

  protected subscriptions: Subscription[] = [];
  public experiments = false;
  public experiments$ = new BehaviorSubject<boolean>(false);

  private pendingMessages: Message[] = [];

  protected analyticsService: AnalyticsService;

  constructor() {
    this.serviceManager = inject(ScrServiceManager);
    this._translate = inject(TranslateService);
    this._log = inject(NGXLogger);
    this.analyticsService = inject(AnalyticsService);

    inject(ConfigService).getConfig().pipe(first()).subscribe(cfg => {
      this.customerSupportPhone = cfg.customerSupportPhone;
    });

    const env = inject(ScrEnvironment);
    this.isThisServiceDesk = env.servicedesk;

    this.experimentsService = inject(ExperimentalFeaturesService);
    this.experiments = this.experimentsService.isEnabled();
    this.subscriptions.push(this.experimentsService.enabled$.subscribe(v => {
      this.experiments = v;
      this.experiments$.next(v);
    }));
  }

  public ngOnDestroy(): void {
    // clear messages when component is being destroyed.
    // this clears local messages but could emit a clear request to
    // the top most message area if no local message component exists.
    this.clearMessages();
    if (this.subscriptions) {
      this.log.trace('unsubscribe from ' + this.subscriptions.length);
      this.subscriptions.forEach(s => s.unsubscribe());
      this.subscriptions = [];
    }
  }

  public ngAfterViewInit(): void {
    if (this.pendingMessages.length > 0) {
      this.pendingMessages.forEach(m => {
        switch (m.type) {
          case 'error': this.msgs?.displayError(m); break;
          case 'warning': this.msgs?.displayWarning(m); break;
          case 'info': this.msgs?.displayInfo(m); break;
          case 'success': this.msgs?.displaySuccess(m); break;
        }
      });
    }
  }
  protected get domSanitizer(): DomSanitizer {
    if (!this._domSanitizer) {
      this._domSanitizer = inject(DomSanitizer);
    }
    return this._domSanitizer;
  }

  protected get log(): NGXLogger {
    if (!this._log) {
      this._log = inject(NGXLogger);// ScrServiceManager.injector!.get(NGXLogger);
    }
    return this._log;
  }

  protected get translate(): TranslateService {
    if (!this._translate) {
      this._translate = inject(TranslateService);//ScrServiceManager.injector!.get(TranslateService);
    }
    return this._translate;
  }

  /**
   * Special case of forkedObserve with two specific types and responses.
   * @param ob1 - first observable
   * @param ob2 - second observable
   * @param onSuccess - callback if all observables complete and messages are success.
   * @param onFailure - if one or more of the requests failed.
   */
  protected observe2<T1, T2>(
    ob1: ObservableInput<T1>,
    ob2: ObservableInput<T2>,
    onSuccess: ((r1: T1, r2: T2) => boolean | void),
    onFailure: ((r1: T1, r2: T2) => boolean | void) = () => { return true; }
  ): Subscription {

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const doResponses = (responses: any[]) => {
      responses.forEach(r => {
        if ((r.status === 'fail') && (r.messages && r.messages.length > 0)) {
          this.doMessages(r);
        }
      });
    };

    return forkJoin([ob1, ob2]).pipe(first()).subscribe({
      next: (rsp: (T1 | T2)[]) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const anyFail = rsp.some((r: any) => r.status === 'fail');
        if (anyFail) {
          const failResult = onFailure.call(this, rsp[0] as T1, rsp[1] as T2);
          if ((failResult !== undefined) && failResult) {
            doResponses(rsp);
          }
        }
        else {
          const successResult = onSuccess.call(this, rsp[0] as T1, rsp[1] as T2);
          if ((successResult !== undefined) && successResult) {
            this.clearMessages();
            window.scrollTo(0, 0);
            this.displayLocalizedSuccess('scr.success', undefined, 'scr.success.title', this.isThisServiceDesk ? 0 : 10_000);
          }
        }
      },
      error: (error) => {
        this.doHttpError(error);
      }
    });
  }

  /**
   * Special case of forkedObserve with two specific types and responses.
   * @param ob1 - first observable
   * @param ob2 - second observable
   * @param onSuccess - callback
   */
  protected observe3<T1, T2, T3>(ob1: ObservableInput<T1>, ob2: ObservableInput<T2>, ob3: ObservableInput<T3>, onSuccess: ((r1: T1, r2: T2, r3: T3) => boolean | void)): Subscription {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.forkedObserve<any>([ob1, ob2, ob3], (rsp: any) => {
      return onSuccess(rsp[0], rsp[1], rsp[2]);
    });
  }



  /**
   * Have an array of requests to be sent to complete your page load?  Use this method.  If there are error messages
   * from any of the responses, they are displayed as errors in the page level error display.
   * 
   * @param obs - an array of observables to observe.
   * @param onSuccess - A onSuccess callback passing you the results of the operation.  Return false to suppress a success message.
   * @returns subscription that completes when all messages return or fail.
   */

  protected forkedObserve<T extends ScrBaseResponse>(obs: ObservableInput<T>[], onSuccess: ((rsp: T[]) => boolean | void)): Subscription {
    return forkJoin(obs).pipe(first()).subscribe({
      next: (rsp: T[]) => {
        let anyErrors = false;
        rsp.forEach(r => {
          if (r.status === 'success') {
            // good...
          }
          else {
            if (r.status === 'fail') {
              anyErrors = true;
            }
            if (r.messages) {
              this.doMessages(r);
            }
          }
        });

        let locallyProcess = true;
        if (typeof onSuccess === 'function') {
          if (!anyErrors) {
            const hookrsp = onSuccess.call(this, rsp);
            if (hookrsp !== undefined) {
              locallyProcess = hookrsp;
            }
          }
        }
        if (locallyProcess && !anyErrors) {
          // retrieve both success message and title
          this.clearMessages();
          window.scrollTo(0, 0);
          this.displayLocalizedSuccess('scr.success', undefined, 'scr.success.title', this.isThisServiceDesk ? 0 : 10_000);
        }
      },
      error: (error) => {
        this.doHttpError(error);
      }
    });
  }

  /**
   * Observer a network observable request.  
   * @param obs - observable of a response extending ScrBaseResponse (which includes status and messages).
   * @param handler - either an object with 3 hook functions or a success handler (most typical).
   * @returns subscription that was created.
   */
  protected observe<T extends ScrBaseResponse>(obs: Observable<T>, handler?: MessageHooks<T> | ((rsp: T) => boolean | void), customLocation?: IuiMessagesComponent): Subscription {

    const observer: PartialObserver<T> = {
      next: (rsp: T) => {
        // http request completed, lets process the result.
        if (rsp.status === 'success') this.doSuccess(rsp, handler, customLocation);
        else if (rsp.messages) this.doMessages(rsp, handler, customLocation);
      },
      error: (error: HttpErrorResponse) => this.doHttpError(error, handler, customLocation),
      complete: () => {
        // nothing to do at this level...
      }
    };

    return obs.pipe(first()).subscribe(observer);
  }

  /**
   * HTTP request completed and the application response status was 'success'.
   * @param rsp - response to be processed.
   * @param handler - application provided hook(s) to invoke.
   */
  private doSuccess<T extends ScrBaseResponse>(rsp: T, handler?: MessageHooks<T> | ((rsp: T) => boolean | void), customLocation?: IuiMessagesComponent) {
    let locallyProcess = true;

    // hooks is a function so call it.  If it returns a value, use it to decide if
    // we should display a success message or not.
    if (typeof handler === 'function') {
      const hookrsp = handler.call(this, rsp);
      if (hookrsp !== undefined) {
        locallyProcess = hookrsp;
      }
    }

    // hooks is an object so call its onSuccess (if provided).
    if (typeof handler === 'object') {
      if (handler.onSuccess) {
        const hookrsp = handler.onSuccess.call(this, rsp);
        if (hookrsp !== undefined) {
          locallyProcess = hookrsp;
        }
      }
    }

    // one of the hooks may have told me not to display success.
    // if that is not the case, look inside the response to see if there is a

    if (locallyProcess) {
      this.clearMessages();
      const message = rsp.messages && rsp.messages[0].message;
      if (message) {
        firstValueFrom(this.translate.get('scr.success.title')).then(title => {
          this.jumpToTop();
          const m = new Message(title, message);
          m.autoHide = this.isThisServiceDesk ? 0 : 10_000;
          if (customLocation) {
            customLocation.displaySuccess(m);
          }
          else {
            this.displaySuccess(m);
          }
        });
      }
      else {
        // retrieve both success message and title
        this.jumpToTop();
        this.displayLocalizedSuccess('scr.success', undefined, 'scr.success.title', this.isThisServiceDesk ? 0 : 10_000, undefined, undefined, customLocation);
      }
    }
  }

  /**
   * The server returned status=200 but the application status was not 'success'.  The server
   * should have returned an array of errors to be displayed.
   * @param rsp - response that includes errors to display
   * @param handler - set of hook functions that might include onFailure to handle the error(s).
   */
  protected doMessages<T extends ScrBaseResponse>(rsp: T, handler?: MessageHooks<T> | ((rsp: T) => boolean | void), customLocation?: IuiMessagesComponent) {
    let locallyProcess = true;
    if (typeof handler === 'object') {
      if (handler.onFailure) {
        const hookrsp = handler.onFailure.call(this, rsp);
        if (hookrsp !== undefined) {
          locallyProcess = hookrsp;
        }
      }
    }
    // If no hook or app returned true (or void), display the error/warning messages.
    if (locallyProcess) {
      if (this.msgs) {
        this.msgs.clearAll();
      }

      if (customLocation) {
        customLocation.clearAll();
      }

      // not a success so it should have messages.
      firstValueFrom(this.translate.get(['scr.error.title', 'scr.warning.title'])).then(translations => {
        if (rsp.messages) {
          this.jumpToTop();
          rsp.messages.map(this.customizeFieldIsRequiredMessage).forEach(m => {
            const title = 'warning' === m.type ? translations['scr.warning.title'] : translations['scr.error.title'];
            const message = new Message(title, m.message);

            // is there a local override for this error message?
            const override = this.translate.instant(`scr.error.${m.code}`, { 'sdPhone': this.customerSupportPhone }) as string;
            if (!override.startsWith('scr')) {
              // there is.  use the override text instead what the back end returned.
              message.text = override;
            }
            if (customLocation) {
              customLocation.displayError(message);
            }
            else if (this.msgs) {
              this.msgs.displayError(message);
            }
            else {
              this.serviceManager?.emitChange({ type: 'error', body: message?.text?.toString(), title: message.title, autoclear: true });
            }

            if (m.field) {
              this.attemptToHighlightFieldInError(m.field);
            }
          });
        }
      });
    }
  }
  attemptToHighlightFieldInError(field: string) {
    this.log.trace('process field in error ' + field);
  }

  protected customizeFieldIsRequiredMessage(m: ScrMessage): ScrMessage {
    if ((m.code === '1200') && (m.field)) {
      // got 'Field is required'.
      const parts = m.field.split('.');
      const field = parts[parts.length - 1]; // try to capture what the name of the field is.
      const fieldInError = startCase(field);
      m.message = fieldInError + ' is required.';
    }
    return m;
  }

  /**
   * Well, bad news folks.   An HTTP error was returned from the server.  Here, the http error is processed.
   * If the error body contains messages that the application created, show those.
   * If the error is a straight HTTP error such as an 500, look up message scr.error.5100 and use it instead
   * of the generic HTTP "Unknown Error" message reported by the browser.
   * @param error - Error to be processed.
   * @param handler - handler functions with possible onError provided to override what we do here.
   */
  protected doHttpError<T extends ScrBaseResponse>(error: HttpErrorResponse, handler?: MessageHooks<T> | ((rsp: T) => boolean | void), customLocation?: IuiMessagesComponent) {
    console.error('error: ' + JSON.stringify(error));
    let locallyProcess = true;
    if (typeof handler === 'object') {
      if (handler.onError) {
        locallyProcess = handler.onError.call(this, error) !== false;
      }
    }
    if (locallyProcess) {
      this.jumpToTop();
      if (this.msgs) {
        this.msgs.clearAll();
      }
      if (customLocation) {
        customLocation.clearAll();
      }

      let errorBody = error.error;
      if (typeof errorBody === 'string') {
        // try to parse it.
        errorBody = JSON.parse(errorBody);
      }
      firstValueFrom(this.translate.get('scr.error.title')).then(title => {
        // if the http error has messages from the server, parse them up and display.
        if (errorBody?.messages) {
          errorBody.messages.map(this.customizeFieldIsRequiredMessage).forEach((m: ScrMessage) => {
            const em = new Message(title, m.message);
            const override = this.translate.instant(`scr.error.${m.code}`, { 'sdPhone': this.customerSupportPhone }) as string;
            if (!override.startsWith('scr')) {
              em.text = override;
            }
            if (customLocation) {
              customLocation.displayError(em);
            }
            else if (this.msgs) {
              this.msgs.displayError(em);
            }
            else {
              this.serviceManager?.emitChange({ type: 'error', body: em?.text?.toString(), title: em.title, autoclear: true });
            }
          });
        }
        else if (error.status) {
          const prefix = this.isThisServiceDesk ? 'scr.httperror.sd.' : 'scr.httperror.';
          const override = this.translate.instant(`${prefix}.${error.status}`, { 'sdPhone': this.customerSupportPhone }) as string;
          if (override && !override.startsWith('scr')) {
            if (customLocation) {
              customLocation.displayError(new Message(title, override));
            }
            else if (this.msgs) {
              this.msgs.displayError(new Message(title, override));
            }
            else {
              this.serviceManager?.emitChange({ type: 'error', body: override, title, autoclear: true });
            }
          }
          else {
            if (customLocation) {
              customLocation.displayError(new Message(title, error.message));
            }
            else if (this.msgs) {
              this.msgs.displayError(new Message(title, error.message));
            }
            else {
              this.serviceManager?.emitChange({ type: 'error', body: error.message, title, autoclear: true });
            }
          }
        }
        else if (error.message.includes('Unknown Error')) {
          // display the generic HTTP error.
          const code = this.isThisServiceDesk ? 'scr.error.sd.5100' : 'scr.error.5100';
          const override = this.translate.instant(code, { 'sdPhone': this.customerSupportPhone }) as string;
          const m = new Message(title, override);
          if (customLocation) {
            customLocation.displayError(m);
          }
          else if (this.msgs) {
            this.msgs.displayError(m);
          }
          else {
            this.serviceManager?.emitChange({ type: 'error', body: override, title, autoclear: true });
          }
        }
        else {
          if (this.msgs) {
            this.msgs.displayError(new Message(title, error.message));
          }
          else {
            this.serviceManager?.emitChange({ type: 'error', body: error.message, title, autoclear: true });
          }
        }
      });
    }
  }

  /**
   * A modified version of the observe method will observe download requests for documents such
   * as PDF or excel generated on the server.  Here, we don't know what the response should contain
   * for HTTP 200, so we just pass it right to the success handler.   For HTTP errors, we process them
   * as usual.
   * @param obs - http network request to watch.
   * @param success - success handler that accepts the message.
   * @returns subscription created.
   */
  protected observeDownloadOrAny<T>(obs: Observable<T>, success: ((rsp: T) => void)): Subscription {
    const observer: PartialObserver<T> = {
      next: (rsp: T) => {
        // i know nothing about the response given that it is an any.
        if (typeof success === 'function') {
          success.call(this, rsp);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.doHttpError(error);
      },
      complete: () => {
        // nothing to do at this level...
      }
    };
    return obs.pipe(first()).subscribe(observer);
  }

  jumpToTop(): void {
    window.scrollTo(0, 0);
  }

  public clearMessages() {
    let done = false;
    if (this.msgs) {
      this.msgs.clearAll();
      done = true;
    }
    if (!done && this.serviceManager) {
      // dispatch request to clear top message.
      this.serviceManager.emitChange({ autoclear: true });
    }
  }

  public createDownloadPartialBlobResponseObserver(): PartialObserver<HttpResponse<Blob>> {
    return {
      next: (rsp: HttpResponse<Blob>) => {
        if (!rsp.body) {
          console.error('missing http response body');
        }
        else {
          const contentDisposition = rsp.headers.get('content-disposition');
          let filename: string;
          if (!contentDisposition) {
            console.error('missing content disposition header');
          }
          else {
            filename = contentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim();
            const file = rsp.body;
            const fileUrl = URL.createObjectURL(file);
            const a = document.createElement('a');

            a.href = fileUrl;
            a.target = '_blank';
            a.download = filename;
            document.body.appendChild(a);
            a.click();
          }
        }
      },
      error: (err: HttpErrorResponse) => {
        this.doHttpError(err);
      }
    };
  }
  public createDownloadPartialObserver(contentType: string, filename: string): PartialObserver<Blob> {
    return {
      next: (data: Blob) => {
        const file = new Blob([data], { type: contentType });
        const fileUrl = URL.createObjectURL(file);
        const a = document.createElement('a');

        a.href = fileUrl;
        a.target = '_blank';
        a.download = filename;
        document.body.appendChild(a);
        a.click();
      },
      error: (err: HttpErrorResponse) => {
        this.doHttpError(err);
      }
    };
  }

  public createPrintPartialObserver(contentType: string): PartialObserver<Blob> {
    return {
      next: (data: Blob) => {
        const file = new Blob([data], { type: contentType });
        const fileUrl = URL.createObjectURL(file);
        const pdf = window.open(fileUrl);
        pdf?.print();
      },
      error: (err: HttpErrorResponse) => {
        this.doHttpError(err);
      }
    };
  }

  /**
   * Use the translate service to look up the message code and insert the provided
   * args into the message.   Display the result as a success message.
   * @param code - code and args to determine message
   * @param args - arguments to plug into message
   * @param titleCode - code from i18n file for title.
   * @param autoHide - time (ms) until message auto-hides (defaults to 10_000)
   * @param dismissible - should the "X" dismiss button be displayed (defaults to true)
   * @param navigateToElem - if provided, try to navigate to a page element (elementId).
   * @param customLocation - if provided, display message in this messages component.
  */
  public displayLocalizedSuccess(code: string, args?: object, titleCode?: string, autoHide = 10_000, dismissible = true, navigateToElem?: string, customLocation?: IuiMessagesComponent) {
    this.translate.get(code, args).subscribe(msg => {
      const m = new Message();
      m.dismissible = dismissible;
      m.type = 'success';
      m.text = msg;
      if (titleCode) {
        m.title = this.translate.instant(titleCode);
      }
      m.autoHide = autoHide;
      if (customLocation) {
        customLocation.displaySuccess(m);
      }
      else if (this.msgs) {
        this.msgs.displaySuccess(m);
      }
      else {
        //console.warn('no value for this.msgs and no customLocation');
        this.pendingMessages.push(m);
      }
      if (navigateToElem) {
        const elmt = document.getElementById(navigateToElem);
        elmt?.scrollIntoView({ behavior: "smooth" });
      }
    });
  }


  /**
 * Use the translate service to look up the message code and insert the provided
 * args into the message.   Display the result as a success message.
 * @param code - code to determine message
 * @param args - arguments to plug into message
 * @param navigateToElem - if provided, try to navigate to a page element (elementId).
 * @param customLocation - if provided, display message in this messages component.
 */
  public displayLocalizedError(code: string, args?: object, navigateToElem?: string, customLocation?: IuiMessagesComponent) {
    this.translate.get([code, 'scr.error.title'], args).subscribe(translations => {
      const m = new Message();
      m.type = 'error';
      m.text = translations[code];
      m.title = translations['scr.error.title'];
      if (customLocation) {
        customLocation.displayError(m);
      }
      else if (this.msgs) {
        this.msgs.displayError(m);
      }
      else {
        this.pendingMessages.push(m);
      }

      if (navigateToElem) {
        const elmt = document.getElementById(navigateToElem);
        elmt?.scrollIntoView({ behavior: "smooth" });
      }

    });
  }

  /**
* Use the translate service to look up the message code and insert the provided
* args into the message.   Display the result as a success message.
 * @param code - code to determine message
 * @param args - arguments to plug into message
 * @param navigateToElem - if provided, try to navigate to a page element (elementId).
 * @param customLocation - if provided, display message in this messages component.
 */
  public displayLocalizedWarning(code: string, args?: object, navigateToElem?: string, customLocation?: IuiMessagesComponent) {
    this.translate.get([code, 'scr.warning.title'], args).subscribe(translations => {
      const m = new Message();
      m.type = 'warning';
      m.text = translations[code];
      m.title = translations['scr.warning.title'];
      if (customLocation) {
        customLocation.displayError(m);
      }
      else if (this.msgs) {
        this.msgs.displayError(m);
      }
      else {
        this.pendingMessages.push(m);
      }

      if (navigateToElem) {
        const elmt = document.getElementById(navigateToElem);
        elmt?.scrollIntoView({ behavior: "smooth" });
      }

    });
  }


  public displaySuccess(message: string | Message, autoHide = false, clearFirst = true, dismissible = false) {
    if (clearFirst) {
      this.msgs?.clearAll();
    }
    if (typeof message == "string") {
      firstValueFrom(this.translate.get('scr.success.title')).then(success => {
        message = new Message(success, message);
        message.type = 'success';
        if (autoHide) {
          message.autoHide = 10_000;
        }
        if (dismissible) {
          message.dismissible = dismissible;
        }
        if (this.msgs) {
          this.msgs.displaySuccess(message);
        }
        else {
          this.pendingMessages.push(message);
        }
      });
    }
    else {
      if (this.msgs) {
        this.msgs.displaySuccess(message);
      }
      else {
        this.pendingMessages.push(message);
      }
    }
  }

  public displayWarningStyledAsError(message: string | Message | MessageCode, clearFirst = true) {
    if (clearFirst) {
      this.msgs?.clearAll();
    }
    if (typeof message == "string") {
      firstValueFrom(this.translate.get('scr.warning.title')).then(w => {
        message = new Message(w, message);
        message.type = 'error';
        if (this.msgs) {
          this.msgs.displayError(message);
        }
        else {
          this.pendingMessages.push(message);
        }
      });
    }
    else if (message instanceof MessageCode) {
      const titleAndMessage = this.translate.instant(['scr.warning.title', message.code]);
      message = new Message(titleAndMessage['scr.warning.title'], titleAndMessage[message.code]);
      message.type = 'error';
      if (this.msgs) {
        this.msgs.displayError(message);
      }
      else {
        this.pendingMessages.push(message);
      }
    }
    else {
      if (this.msgs) {
        this.msgs.displayError(message);
      }
      else {
        this.pendingMessages.push(message);
      }
    }
  }

  processHttpError(error: HttpErrorResponse) {
    console.error('error: ' + JSON.stringify(error));
    this.clearMessages();
    const em = new Message(undefined, error.message);
    em.type = 'error';
    if (this.msgs) {
      this.msgs.displayError(em);
    }
    else {
      this.pendingMessages.push(em);
    }
  }

  protected processScrErrorResponse(err: ScrBaseResponse, jumpTop = true): void {
    if (err.messages) {
      this.clearMessages();
      firstValueFrom(this.translate.get('scr.error.title')).then(title => {
        if (err.messages) {
          err.messages.forEach(m => {
            const em = new Message(title, m.message);
            em.type = m.type ?? 'error';
            if (m.field) {
              em.field = m.field;
            }
            if (this.msgs) {
              this.msgs.displayError(em);
            }
            else {
              this.pendingMessages.push(em);
            }
          });
          if (jumpTop) {
            this.jumpToTop();
          }
        }
      });
    }
  }

  protected processHttpScrErrorResponse(err: HttpErrorResponse): void {
    if (err.error?.messages) {
      this.clearMessages();
      firstValueFrom(this.translate.get('scr.error.title')).then(title => {
        if (err.error?.messages) {
          err.error.messages.forEach((m: ScrMessage) => {
            const em = new Message(undefined, m.message);
            em.type = 'error';
            if (this.msgs) {
              this.msgs.displayError(em);
            }
            else {
              this.pendingMessages.push(em);
            }
          });
        }
        else {
          this.displayErrorMessage(new Message(title, err.message));
        }
      });
    }
  }


  protected localizeTableColumns(columns: TableColumn[], prefix = 'scr.tc'): void {
    if (!columns || columns.length === 0) {
      return;
    }
    const keys = columns.map(c => `${prefix}.${c.localizationKey ?? c.field}`);
    firstValueFrom(this.translate.get(keys)).then(translations => {
      columns.forEach(tc => {
        const key = `${prefix}.${tc.localizationKey ?? tc.field}`;
        const trans = translations[key];
        if (trans !== key && !tc.header) {
          tc.header = trans;
        }
      });
    });
  }



  // pages that have child editor panes and a list receive actions from
  // their children.  Mostly to display a message and reload.  We process
  // those events with these methods.

  receiveChildEvent(ev: ChildEvent) {
    if (ev) {
      this.hideChildren();
      this.reloadList(ev.message, ev.messageCode, ev.value);
    }
  }

  protected hideChildren(): void {
    // nothing to do.  subclass should handle this.
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected reloadList(message?: string, messageCode?: string, value?: number | string): void {
    // nothing to do.  subclass should handle this.
  }


  /**
   * Are there any messages being displayed?
  */
  public hasMessages(): boolean {
    if (this.msgs) {
      return this.msgs.hasMessages();
    }
    else {
      return false;
    }
  }



  /**
   * Clear errors from the messages area.
   */
  public clearErrors(): void {
    if (this.msgs) {
      this.msgs.clearErrors();
    }
  }

  /**
   * Clear success messages only.
   */
  public clearSuccessMessages(): void {
    if (this.msgs) {
      this.msgs.clearSuccessMessages();
    }
  }

  /**
   * Display a success message in the message area.   Success messages are styled (as of this writing) with
   * a light green background.
   * @param title - title of the message (in bold) such as "Success!"
   * @param body - text of the message - should probably be kept to a single phrase.
   * @param autoHide - number of milliseconds to display before hidden (defaults to zero which turns off autohide)
   * @param dismissible - if true, an "X" appears allowing the user to manually dismiss the message.
   */
  public displaySuccessMessage(title: string, body: string, autoHide = 10000, dismissible = true): void {
    const m = new Message();
    m.title = title;
    m.text = body;
    m.autoHide = autoHide;
    m.dismissible = dismissible;
    this.displaySuccess(m);
  }

  /**
   * Use the translate service to look up the message code and insert the provided
   * args into the message.   Display the result as a success message.
   * @param ca - code and args to determine message
   * @param titleCode - optional code to lookup title for message.
   * @param authHide - time for auto hide of message (defaults to 10,000ms)
   * @param dismissible - true if the "X" dismiss icon should appear in the error display.
   */
  public displayTranslatedSuccess(ca: CodeAndArgs, titleCode?: string, autoHide = 10000, dismissible = true): void {
    this.translate.get(ca.code, ca.args).pipe(first()).subscribe(msg => {
      const m = new Message();
      m.dismissible = dismissible;
      m.text = msg;
      if (titleCode) {
        m.title = this.translate.instant(titleCode);
      }
      m.autoHide = autoHide;
      if (this.msgs) {
        this.msgs.displaySuccess(m);
      }
      else {
        this.log.warn('no messages or combined messages component to display success message');
      }
    });
  }

  /**
   * Use the translate service to look up the message code and insert the provided
   * args into the message.   Display the result as an error message.
   * @param ca - code and args to determine message
   */
  public displayTranslatedErrorMessage(ca: CodeAndArgs): void {
    this.translate.get(ca.code, ca.args).pipe(first()).subscribe(m => {
      this.displayErrorMessage(m);
    });
  }

  /**
   * Display an error message.
   * @param msg -- either string (dismissible error text) or Message instance.
   * @param title - optional title to appear in the bold with the message;
   */
  public displayErrorMessage(msg: string | Message, title?: string): void {
    let m: Message;
    if (typeof msg === 'string') {
      m = new Message();
      if (title) {
        m.title = title;
      }
      m.dismissible = true;
      m.text = msg;
    }
    else {
      m = msg;
    }
    if (this.msgs) {
      this.msgs.displayError(m);
    }
    else {
      this.log.warn('no messages or combined messages component found.  cannot show ' + msg);
    }
  }


  /**
   * Use the translate service to look up the message code and insert the provided
   * args into the message.   Display the result as an info message.
   * @param ca - code and args to determine message
   */
  public displayTranslatedInfoMessage(ca: CodeAndArgs): void {
    this.translate.get(ca.code, ca.args).pipe(first()).subscribe(m => {
      this.displayInfoMessage(m);
    });
  }

  /**
   * Display an info message.
   * @param msg - either string (dismissible info text) or Message instance.
   */
  public displayInfoMessage(msg: string | Message): void {
    let m: Message;
    if (typeof msg === 'string') {
      m = new Message();
      m.dismissible = false;
      m.text = msg;
    }
    else {
      m = msg;
    }

    if (this.msgs) {
      this.msgs.displayInfo(m);
    }
    else {
      this.log.warn('no messages or combined messages component found.  cannot show ' + msg);
    }
  }


  /**
   * Use the translate service to look up the message code and insert the provided
   * args into the message.   Display the result as an info message.
   * @param ca - code and args to determine message
   */
  public displayTranslatedWarningMessage(ca: CodeAndArgs): void {
    this.translate.get(ca.code, ca.args).pipe(first()).subscribe(m => {
      this.displayWarningMessage(m);
    });
  }

  /**
   * Display an info message.
   * @param msg - either string (dismissible info text) or Message instance.
   */
  public displayWarningMessage(msg: string | Message): void {
    let m: Message;
    if (typeof msg === 'string') {
      m = new Message();
      m.dismissible = false;
      m.text = msg;
    }
    else {
      m = msg;
    }
    if (this.msgs) {
      this.msgs.displayWarning(m);
    }
    else {
      this.log.warn('no messages or combined messages component found.  cannot show ' + msg);
    }

  }

  /**
   * Display messages in a response object as error messages.
   * @param rsp - response from the server containing either 'message' or 'messages'
   */
  public processMessages(rsp: IScrBaseResponse | ScrBaseResponse): void {
    if (rsp.messages) {
      rsp.messages.forEach((msg: ScrMessage) => {
        const m = new Message();
        if (msg.message) {
          m.text = msg.message;
        }
        else {
          m.text = msg;
        }
        if (this.msgs) {
          this.msgs.displayError(m);
        }
      });
    }
  }

  /**
   * Get the first message from a response if messages are present.
   */
  getFirstMessage(rsp: IScrBaseResponse | ScrBaseResponse): string | undefined {
    let rv;
    if (rsp.messages && rsp.messages.length > 0) {
      rv = rsp.messages[0].message;
    }
    return rv;
  }


  scrollToFirstError(): HTMLElement | null | undefined {

    let el = document.querySelector(':not(form):not(div).ng-invalid');
    const path: string[] = [];
    while (el) {
      path.push(el.tagName);
      el = el.parentElement;
    }
    //this.log.trace('path to first error ' + path.reverse().join(' '));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const firstError = document.querySelector(':not(form):not(div).ng-invalid')?.parentElement as any;
    if (firstError) {
      if (firstError.scrollIntoViewIfNeeded) {
        firstError.scrollIntoView();
      }
      else {
        firstError.scrollIntoView();
      }
    }
    return firstError;
  }


  handleEnterKeyPress(event: Event): void {
    if (event.target) {
      const tagName = (event.target as HTMLElement).tagName.toLowerCase();
      if (tagName === 'textarea') {
        return;
      }
    }
    return event.preventDefault();
  }


  updateFormControlValidity(form: NgForm) {
    setTimeout(() => {
      for (const name in form.controls) {
        form.controls[name].updateValueAndValidity();
      }
    }, 100);
  }
}



/**
 * A component that can live in tabs on the my short codes or billing pages.
 */

@Component({
  template: ''
})
export abstract class ScrBaseTabComponent extends ScrBaseComponent {

  public tt = '';
  public routeElement = 'invoice';

  abstract getObjectId(): string;

  /**
   * By default, just return the object id.
   * However, some pages need to override this.
   */
  public getPathSegmentValues(): string[] {
    return [this.getObjectId()];
  }

}
